import React from 'react';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PageHeader } from 'src/modules/shared/components/molecules/page-header';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { PropertiesSearch } from '../../schemas/properties-search.schema';
import type { PropertyPublicationsSearch } from '../../schemas/property-publications-search.schema';
import { PropertyPublicationsContent } from '../organisms/property-publications-content';
import { PropertyPublicationsManagedModal } from '../organisms/property-publications-managed-modal';
import { usePropertyPublicationsPage } from '../../hooks/use-property-publications-page';

interface PropertyPublicationsPageProps {
  backSearch: PropertiesSearch;
  search: PropertyPublicationsSearch;
}

export const PropertyPublicationsPage: React.FC<
  PropertyPublicationsPageProps
> = ({ backSearch, search }) => {
  const { t } = useI18n();
  const {
    handleBack,
    handleNextPage,
    handleOpenProperty,
    handlePageSizeChange,
    handlePublicationPortalChange,
    handlePublicationStatusChange,
    isMeliConnected,
    isMeliSyncMode,
    isToolbarExpanded,
    loadingIntegrations,
    managedOperation,
    managedProperty,
    managedPropertyPublicationsQuery,
    managedPublication,
    pageSize,
    propertyById,
    loadingPropertyIds,
    publicationPortal,
    publicationStatus,
    publicationsQuery,
    setIsToolbarExpanded,
    setManagedPublicationId,
  } = usePropertyPublicationsPage({
    search,
    backSearch,
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Panel className="flex h-full min-h-0 flex-col">
        <PageHeader
          title={t('catalog.properties.publications.page.title')}
          subtitle={t('catalog.properties.publications.page.subtitle')}
          onBack={handleBack}
          backLabel={t('catalog.properties.editor.backToList')}
        />

        <div className="flex-1 min-h-0 overflow-hidden">
          <PropertyPublicationsContent
            loadingIntegrations={loadingIntegrations}
            error={publicationsQuery.error}
            handleBack={handleBack}
            refetch={publicationsQuery.refetch}
            publications={publicationsQuery.publications}
            loading={publicationsQuery.loading}
            propertyById={propertyById}
            loadingPropertyIds={loadingPropertyIds}
            handleOpenProperty={handleOpenProperty}
            isMeliConnected={isMeliConnected}
            isMeliSyncMode={isMeliSyncMode}
            onManagePublication={(publicationId) =>
              setManagedPublicationId(publicationId)
            }
            pageSize={pageSize}
            totalCount={publicationsQuery.totalCount}
            handlePageSizeChange={handlePageSizeChange}
            handleNextPage={handleNextPage}
            hasNextPage={publicationsQuery.nextPageToken != null}
            publicationStatus={publicationStatus}
            publicationPortal={publicationPortal}
            onPublicationStatusChange={handlePublicationStatusChange}
            onPublicationPortalChange={handlePublicationPortalChange}
            isToolbarExpanded={isToolbarExpanded}
            onToolbarExpandedChange={setIsToolbarExpanded}
          />
        </div>

        <PropertyPublicationsManagedModal
          managedPublication={managedPublication}
          managedProperty={managedProperty}
          managedOperation={managedOperation}
          managedPropertyPublications={
            managedPropertyPublicationsQuery.publications
          }
          publications={publicationsQuery.publications}
          isMeliSyncMode={isMeliSyncMode}
          onClose={() => {
            setManagedPublicationId(null);
          }}
          refetchManagedPropertyPublications={
            managedPropertyPublicationsQuery.refetch
          }
          refetch={publicationsQuery.refetch}
        />
      </Panel>
    </div>
  );
};
